import { useState } from "react";
import { Link, Redirect, useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import {
  useStartEmailChange,
  useCancelEmailChange,
  useVerifyEmailChange,
} from "@orbit/api-client-react";
import { useAuth } from "../hooks/useAuth";
import { Wordmark } from "../components/Brand";
import { OtpVerificationStep } from "../components/OtpVerificationStep";

type Step =
  | { kind: "enter-email" }
  | {
      kind: "verify";
      changeRequestId: string;
      maskedNewEmail: string;
      expiresAt: string;
    }
  | { kind: "done"; newEmail: string };

/**
 * /profile/email-change — two-step (enter email → enter code).
 *
 * Email change is a single-OTP flow (D32) sent to the NEW address.
 * The current address gets a heads-up email but doesn't gate the
 * change — proving control of the destination is sufficient for an
 * email change because (a) email isn't a recovery factor as strong
 * as phone, and (b) requiring two OTPs across SMS+email would force
 * SMTP into the critical path of an SMS flow.
 *
 * If the user has no email yet (`first-time-email` case), the flow is
 * the same — start sets the address, verify proves it.
 */
export function ProfileEmailChangePage() {
  const { isAuthenticated } = useAuth();
  const [, navigate] = useLocation();
  const qc = useQueryClient();

  const [step, setStep] = useState<Step>({ kind: "enter-email" });
  const [newEmail, setNewEmail] = useState("");
  const [error, setError] = useState<string | null>(null);

  const startMut = useStartEmailChange();
  const cancelMut = useCancelEmailChange();
  // Verify can also happen on /verify-email (deep link from the email
  // body); this surface lets the user paste the code instead.
  const verifyMut = useVerifyEmailChange();

  if (!isAuthenticated) return <Redirect to="/" />;

  async function onStart(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const r = await startMut.mutateAsync({ newEmail: newEmail.trim() });
      setStep({
        kind: "verify",
        changeRequestId: r.changeRequestId,
        maskedNewEmail: r.maskedNewEmail,
        expiresAt: r.expiresAt,
      });
    } catch (err) {
      const e = err as { apiError?: { code?: string; message?: string } };
      if (e.apiError?.code === "EMAIL_ALREADY_REGISTERED") {
        setError("That email is already on another account.");
      } else if (e.apiError?.code === "SAME_EMAIL") {
        setError("That's already your current email.");
      } else if (e.apiError?.code === "CHANGE_REQUEST_PENDING") {
        setError("You already have a pending email change. Cancel it first.");
      } else {
        setError(e.apiError?.message ?? "Couldn't start the change.");
      }
    }
  }

  async function cancel() {
    if (step.kind !== "verify") {
      navigate("/profile");
      return;
    }
    try {
      await cancelMut.mutateAsync({ changeRequestId: step.changeRequestId });
    } catch {
      // best effort
    }
    navigate("/profile");
  }

  return (
    <div className="min-h-screen pb-16">
      <div className="mx-auto max-w-md px-6 pt-8">
        <header className="flex items-center justify-between">
          <Wordmark size="sm" />
          <Link href="/profile" className="text-xs text-ink-dark underline">
            Back
          </Link>
        </header>
        <h1 className="mt-6 text-xl font-bold text-navy">Change your email</h1>
        <p className="mt-2 text-sm text-ink-dark">
          We'll send a 6-digit code to your new address.
        </p>

        {step.kind === "enter-email" && (
          <form onSubmit={onStart} className="mt-6 space-y-3">
            <label className="block">
              <span className="text-xs uppercase tracking-wider text-ink-dark/70">
                New email
              </span>
              <input
                className="input mt-1"
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                required
              />
            </label>
            {error && <p className="text-red-600 text-sm">{error}</p>}
            <button
              type="submit"
              className="btn-primary w-full"
              disabled={startMut.isPending}
            >
              {startMut.isPending ? "Sending code…" : "Send code"}
            </button>
            <button
              type="button"
              onClick={() => navigate("/profile")}
              className="text-xs text-ink-dark underline w-full text-center"
            >
              Cancel
            </button>
          </form>
        )}

        {step.kind === "verify" && (
          <div className="mt-6 card">
            <OtpVerificationStep
              title="Enter the code we emailed you"
              body={
                <>
                  Code sent to{" "}
                  <span className="font-medium">{step.maskedNewEmail}</span>.
                  Or open the link in that email to verify.
                </>
              }
              expiresAt={step.expiresAt}
              busy={verifyMut.isPending}
              secondaryAction={{ label: "Cancel change", onClick: cancel }}
              onSubmit={async (code) => {
                const r = await verifyMut.mutateAsync({
                  changeRequestId: step.changeRequestId,
                  code,
                });
                await qc.invalidateQueries({ queryKey: ["customer-me"] });
                await qc.invalidateQueries({ queryKey: ["customer-profile"] });
                setStep({ kind: "done", newEmail: r.newEmail });
              }}
            />
          </div>
        )}

        {step.kind === "done" && (
          <div className="mt-6 card border-green-200 bg-green-50">
            <h2 className="text-base font-semibold text-green-900">
              Email updated
            </h2>
            <p className="mt-2 text-sm text-green-900">
              Your new email {step.newEmail} is now on file and verified.
            </p>
            <Link
              href="/profile"
              className="mt-4 inline-block text-sm font-medium underline text-green-900"
            >
              Back to profile →
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
